import React, { useState, useEffect, useCallback } from 'react';
import { supabase, supabaseUrl } from '../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaCog, FaInfoCircle } from 'react-icons/fa';
import AdminSidebar from '../components/AdminSidebar';

const AdminSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*');

      if (error) throw error;
      
      const settingsMap = {};
      data.forEach(setting => {
        settingsMap[setting.key] = setting.value;
      });
      setSettings(settingsMap);
    } catch (error) {
      console.error('Error fetching settings:', error);
      alert('Error al cargar la configuración del sitio.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    fetchSettings();
  }, [checkAuth, fetchSettings]);

  const handleChange = (key, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [key]: value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Iterate over settings and update each one
      for (const key in settings) {
        if (settings.hasOwnProperty(key)) {
          const functionUrl = `${supabaseUrl}/functions/v1/update-site-setting`; // Needs to be created
          const { apikey, Authorization } = supabase.auth.headers;
          
          const response = await fetch(functionUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', apikey, Authorization },
            body: JSON.stringify({
              key: key,
              value: settings[key]
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Error al actualizar la configuración '${key}': ${response.status} ${errorText}`);
          }
        }
      }
      alert('Configuración guardada exitosamente.');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert(error.message || 'Error al guardar la configuración.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando configuración...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      
      <div className="flex-1">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-3">
              <FaCog className="text-blue-600" size={32} />
              <div>
                <h1 className="text-3xl font-playfair font-bold text-gray-900">
                  Ajustes del Sitio
                </h1>
                <p className="text-sm text-gray-600 mt-1">Configura la información general del sitio web</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <main className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSave} className="space-y-6">
              {/* Email Section */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <FaInfoCircle className="text-blue-600" />
                    <h3 className="text-lg font-bold text-gray-900">Información de Contacto</h3>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email de Contacto
                    </label>
                    <input
                      type="email"
                      id="contact_email"
                      value={settings.contact_email || ''}
                      onChange={(e) => handleChange('contact_email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="contacto@ejemplo.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact_phones" className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfonos de Contacto
                    </label>
                    <input
                      type="text"
                      id="contact_phones"
                      value={JSON.parse(settings.contact_phones || '[]').join(', ') || ''}
                      onChange={(e) => handleChange('contact_phones', JSON.stringify(e.target.value.split(',').map(s => s.trim())))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+51 987 654 321, +51 123 456 789"
                    />
                    <p className="text-xs text-gray-500 mt-2">Separa múltiples teléfonos con comas</p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="sticky bottom-6">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-4 rounded-xl hover:from-green-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <FaSave size={20} />
                      Guardar Cambios
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminSettings;
